import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { AppLayout } from "../components/layout";
import { Card, CardContent } from "@/components/ui";
import { AlertCircle, HelpCircle } from "lucide-react";
import { getHelpCenters, createHelpCenter, deleteHelpCenter } from "../services/api";
import type { HelpCenter } from "../types";
import { EmptyState } from "@/components/ui/empty-state";
import { useUIStore } from "../store/uiStore";
import { useBillingInfo, useIsProCloudPlan } from "../store/userStore";
import UpsellModal from "../components/modals/UpsellModal";
import { formatDate } from "../lib/i18n-dates";
import {
  HelpCentersTable,
  CreateHelpCenterModal,
  DeleteHelpCenterModal,
  HelpCentersPageHeader,
  HelpCentersLoadingState,
  HelpCenterSettingsDrawer,
} from "../components/features/help-center";

const HelpCentersPage: React.FC = () => {
  const { t } = useTranslation();
  const [helpCenters, setHelpCenters] = useState<HelpCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [helpCenterToDelete, setHelpCenterToDelete] = useState<number | null>(null);
  const [helpCenterToDeleteTitle, setHelpCenterToDeleteTitle] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormError, setCreateFormError] = useState<string | null>(null);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [helpCenterToEdit, setHelpCenterToEdit] = useState<number | null>(null);
  const { openModal, closeModal, modals } = useUIStore();
  const isProCloudPlan = useIsProCloudPlan();
  const billingInfo = useBillingInfo();
  
  const breadcrumbs = [
    { title: t("dashboard.breadcrumb"), href: "dashboard" },
    { title: t("sidebar.helpCenters"), isCurrentPage: true },
  ];

  useEffect(() => {
    fetchHelpCenters();
  }, []);

  const fetchHelpCenters = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getHelpCenters();
      if (response.ok && response.helpCenters) {
        const sorted = response.helpCenters.sort(
          (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
        setHelpCenters(sorted);
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0] 
          : response.error || t("helpCenters.fetchFailed");
        setError(errorMessage);
      }
    } catch (err) {
      console.error("Error fetching help centers:", err);
      setError(t("helpCenters.fetchFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHelpCenter = (helpCenterId: number) => {
    const helpCenter = helpCenters.find((hc) => hc.id === helpCenterId);
    setHelpCenterToDelete(helpCenterId);
    setHelpCenterToDeleteTitle(helpCenter?.title || null);
    openModal('delete');
  };

  const handleConfirmDelete = async () => {
    if (!helpCenterToDelete) return;
    
    closeModal('delete');
    setIsDeleting(true);
    
    try {
      const result = await deleteHelpCenter(helpCenterToDelete.toString());
      if (result.ok) {
        toast.success(t("helpCenters.deletedSuccess"), {
          position: "top-center",
          duration: 3000,
        });
        
        await fetchHelpCenters();
        setHelpCenterToDelete(null);
        setHelpCenterToDeleteTitle(null);
      } else {
        const errorMessage = result.errors && result.errors.length > 0 
          ? result.errors[0] 
          : result.error || t("helpCenters.deleteFailed");
        toast.error(errorMessage, {
          position: "top-center",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error deleting help center:', error);
      toast.error(t("helpCenters.deleteFailed"), {
        position: "top-center",
        duration: 3000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    closeModal('delete');
    setHelpCenterToDelete(null);
    setHelpCenterToDeleteTitle(null);
  };

  const handleCreateHelpCenter = async (form: { title: string; description: string; slug: string }) => {
    // Check limits before attempting to create
    if (!isProCloudPlan) {
      const helpCentersLimit = billingInfo?.helpCentersLimit ?? 1;
      const helpCenterCreated = billingInfo?.helpCenterCreated ?? 0;
      if (helpCenterCreated >= helpCentersLimit) {
        setCreateFormError(t("helpCenters.freePlanLimit", { count: helpCentersLimit }));
        openModal('upsell');
        return;
      }
    }

    // Clear previous errors
    setCreateFormError(null);
    setIsCreating(true);
    
    try {
      const result = await createHelpCenter({
        title: form.title,
        description: form.description || undefined,
        slug: form.slug || undefined,
      });

      if (result.ok && result.helpCenter) {
        toast.success(t("helpCenters.createdSuccess"), {
          position: "top-center",
          duration: 3000,
        });
        
        setShowCreateModal(false);
        setCreateFormError(null);
        await fetchHelpCenters();
      } else {
        // Display server-side validation error in modal
        // Handle both error (string) and errors (array) formats
        const errorMessage = result.errors && result.errors.length > 0 
          ? result.errors[0] 
          : result.error || t("helpCenters.createFailed");
        setCreateFormError(errorMessage);
        // If error mentions limit or upgrade, show upsell modal
        if (errorMessage.toLowerCase().includes('limit') || errorMessage.toLowerCase().includes('upgrade')) {
          openModal('upsell');
        }
      }
    } catch (error) {
      console.error('Error creating help center:', error);
      setCreateFormError(t("helpCenters.createFailedRetry"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditHelpCenter = (helpCenterId: number) => {
    setHelpCenterToEdit(helpCenterId);
    setShowEditDrawer(true);
  };

  const handleEditSuccess = () => {
    fetchHelpCenters();
  };

  if (loading) {
    return <HelpCentersLoadingState breadcrumbs={breadcrumbs} />;
  }

  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <div className="p-4">
        <HelpCentersPageHeader onCreateClick={() => setShowCreateModal(true)} />

        {/* Error State */}
        {error && (
          <Card className="border-destructive mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Centers Table */}
        {helpCenters.length === 0 && !error ? (
          <EmptyState
            title={t("helpCenters.emptyTitle")}
            description={t("helpCenters.emptyDescription")}
            icons={[HelpCircle, HelpCircle, HelpCircle]}
            className="mx-auto"
          />
        ) : (
          <HelpCentersTable
            helpCenters={helpCenters}
            onEdit={handleEditHelpCenter}
            onDelete={handleDeleteHelpCenter}
            formatDate={formatDate}
          />
        )}
      </div>

      <CreateHelpCenterModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateFormError(null);
        }}
        onSubmit={handleCreateHelpCenter}
        isCreating={isCreating}
        error={createFormError}
      />

      <HelpCenterSettingsDrawer
        isOpen={showEditDrawer}
        onClose={() => {
          setShowEditDrawer(false);
          setHelpCenterToEdit(null);
        }}
        helpCenterId={helpCenterToEdit}
        onSuccess={handleEditSuccess}
      />

      <DeleteHelpCenterModal
        isOpen={modals.delete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
        helpCenterTitle={helpCenterToDeleteTitle || undefined}
      />

      <UpsellModal
        isOpen={modals.upsell}
        onClose={() => closeModal("upsell")}
      />
    </AppLayout>
  );
};

export default HelpCentersPage;

